'use client';

import type { PropsWithChildren } from 'react';
import type { User } from '@/types/user/type';
import type { ContributionType } from '~/model/ContributionType';
import type { InteractionType } from '~/model/InteractionType';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';
import { CrossSell, CrossSellWrapper } from '@/app/[locale]/(form)/components/CrossSell';
import { fetchItem } from '@/app/actions/server';
import { Loader } from '@/app/components/Loader';
import { useProgressContext } from '@/app/components/Provider';
import { useSlideAnimationContext } from '@/app/components/Provider/SlideAnimationProvider';
import { useUserCriteria } from '@/app/hooks/useUserCriteria';
import { canInitialize } from '@/app/utils/contributions/initialize';
import { redirect, usePathname } from '@/i18n/routing';
import { CriterionForm } from '~/components/commons/Criteria/CriterionForm';
import { ProfileCompletionSports } from '~/components/Interaction/Screens/ProfileCompletionSports/ProfileCompletionSports';
import appConst from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';
import { useProfileCompletionContext } from '../provider/ProfileCompletionProvider';
import { SorryPage } from '../SorryPage/SorryPage';
import { MandatoryCriteriaForm } from './MandatoryCriteriaForm/MandatoryCriteriaForm';

export const CriteriaCompletion = ({
  children,
  contributor,
  interaction,
  refreshContributions,
}: PropsWithChildren<{
  contributor: User;
  interaction?: InteractionType;
  refreshContributions?: () => Promise<void>;
}>) => {
  const pathname = usePathname();
  const locale = useLocale();

  const { total, setIndex } = useProgressContext();
  const { animateSlide } = useSlideAnimationContext();

  const [currentStatus, setCurrentStatus] = useState(interaction?.current_user_interaction_status);

  const {
    displayCriterionForm,
    displayMandatoryCriteria,
    displayMandatoryCriteriaEnd,
    displaySportsCompletion,
    requestedCriteria,
    requestedCriteriaChoiceUser,
    setRequestedCriteria,
    mandatoryCriteria,
    mandatoryCriteriaChoiceUser,
    setIsMandatoryCompletionClosed,
    setIsSportsCompletionClosed,
  } = useProfileCompletionContext();

  const handleRefreshCurrentStatus = async () => {
    const response = await fetchItem<InteractionType>({
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      pathname: `interactions/${interaction.id}`,
      revalidate: 0,
      searchParams: {
        ...contributor.child && {
          child: contributor.id,
        },
        'groups[]': [
          'ed_interaction_user_interaction_status',
          'ed_interaction_user_contributions',
          'interaction_translations',
          'project_media',
          'read_interaction_parent',
        ],
      },
    });

    const status = response.current_user_interaction_status;

    if (status.user_status === 'MATCH' && canInitialize({ interaction: response })) {
      await fetchItem<ContributionType>({
        pathname: 'contributions',
        method: 'POST',
        revalidate: 0,
        body: {
          interaction: interaction['@id'],
          created_for: contributor['@id'],
        },
      });

      await refreshContributions();
    }

    return status;
  };

  const { submit, isFetching, isUpdating } = useUserCriteria({
    child: contributor.child && contributor,
    interaction: interaction?.['@id'],
    onSubmit: async ({ callback }) => {
      const status = currentStatus && await handleRefreshCurrentStatus();

      await animateSlide({
        callback: async () => {
          if (!displayMandatoryCriteria) {
            setRequestedCriteria(([_, ...rest]) => rest);
          }

          setIsMandatoryCompletionClosed(true);
          setCurrentStatus(status);
          setIndex(current => ++current);
          await callback();
        },
      });
    },
  });

  useEffect(() => {
    if (!total && pathname === appConst.staticRoutes.main_criterias) {
      redirect({ href: { pathname: appConst.staticRoutes.profile }, locale });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentStatus?.user_status === 'MISMATCH') {
    return (
      <SorryPage
        interaction={interaction}
        mismatched={currentStatus.criterion_status.mismatched}
      >
        <CrossSellWrapper>
          <CrossSell projectId={interaction.project.id} />
        </CrossSellWrapper>
      </SorryPage>
    );
  }

  if (displayMandatoryCriteriaEnd) {
    return <>{children}</>;
  }

  return (
    <Loader loading={isFetching}>
      {displayMandatoryCriteria && (
        <MandatoryCriteriaForm
          criteria={mandatoryCriteria}
          isLoading={false}
          user={contributor}
          userCriteria={mandatoryCriteriaChoiceUser}
          submit={submit}
        />
      )}
      {displaySportsCompletion && (
        <ProfileCompletionSports
          {...contributor.child && { child: contributor }}
          onClose={async () => {
            if (currentStatus) {
              await handleRefreshCurrentStatus();
            }

            await animateSlide({
              callback: async () => {
                setIsSportsCompletionClosed(true);
                setIndex(current => ++current);
              },
            });
          }}
        />
      )}
      {displayCriterionForm && (
        <CriterionForm
          description="HOMEPAGE.COMPLETE_PROFILE.CRITERIA"
          userCriteria={requestedCriteriaChoiceUser}
          criterion={requestedCriteria[0]}
          user={contributor}
          {...!interaction && { isLast: requestedCriteria?.length <= 1 }}
          isProcessing={isFetching || isUpdating}
          submit={submit}
        />
      )}
    </Loader>
  );
};
